import { formatCurrency } from '@/lib/utils';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Wallet, ArrowUpRight, ArrowDownRight, Minus, Filter, X, ExternalLink, ChevronLeft, ChevronRight, RefreshCw, Calculator, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import { Lock } from 'lucide-react';
import { useLockedPeriods } from '../hooks/useLockedPeriods';

const LedgerPage = () => {
  const { api, user } = useAuth();
  const { lockedPeriodFor } = useLockedPeriods();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [ledgerData, setLedgerData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  
  // Filter states - no default dates
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchLedger(selectedBusiness);
      setCurrentPage(1); // Reset to first page when business changes
    }
  }, [selectedBusiness]);

  const fetchBusinesses = async () => {
    try {
      const [businessesRes, summaryRes] = await Promise.all([
        api.get('/api-keys/all'),
        api.get('/ledger-summary')
      ]);
      setBusinesses(businessesRes.data);
      setSummary(summaryRes.data);
      if (businessesRes.data.length > 0) {
        setSelectedBusiness(businessesRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async (businessId) => {
    try {
      const res = await api.get(`/ledger/${businessId}`);
      setLedgerData(res.data);
    } catch (error) {
      toast.error('Failed to load ledger');
    }
  };

  const handleRecalculateBalances = async () => {
    setRecalculating(true);
    setShowRecalcConfirm(false);
    try {
      const res = await api.post(`/admin/recalculate-ledger/${selectedBusiness}`);
      toast.success(`Recalculated ${res.data.entry_count} entries. New balance: ${formatCurrency(res.data.new_balance)}`);
      // Refresh data
      fetchLedger(selectedBusiness);
      fetchBusinesses(); // Refresh summary too
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate balances');
    } finally {
      setRecalculating(false);
    }
  };

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    setShowRecalcConfirm(false);
    try {
      const res = await api.post('/admin/recalculate-all-ledgers');
      const totalEntries = res.data.results.reduce((sum, r) => sum + r.entry_count, 0);
      toast.success(`Recalculated ${totalEntries} entries across ${res.data.results.length} businesses`);
      // Refresh data
      fetchLedger(selectedBusiness);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate balances');
    } finally {
      setRecalculating(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const handleExportExcel = async () => {
    try {
      let url = '/ledger/export/excel';
      const params = new URLSearchParams();
      if (selectedBusiness) params.append('business_id', selectedBusiness);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (params.toString()) url += '?' + params.toString();
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Get filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'ledger_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Ledger exported to Excel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export ledger');
    }
  };

  const getEntryIcon = (type) => {
    switch (type) {
      case 'cash_in':
        return <ArrowUpRight className="h-4 w-4 text-green-600" />;
      case 'payout':
      case 'expense':
        return <ArrowDownRight className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const handleEntryClick = (entry) => {
    if (entry.reference_type === 'cashup' && entry.reference_id) {
      navigate(`/cashup/${entry.reference_id}`);
    } else if (entry.reference_type === 'expense' && entry.reference_id) {
      navigate('/expenses');
      toast.info('Navigated to Cash Movement page');
    }
  };

  const isClickable = (entry) => {
    return entry.reference_id && (entry.reference_type === 'cashup' || entry.reference_type === 'expense');
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  // Apply filters to ledger data
  const filteredLedgerData = useMemo(() => {
    return ledgerData.filter(entry => {
      const entryDate = entry.entry_date?.split('T')[0];
      if (dateFrom && entryDate < dateFrom) return false;
      if (dateTo && entryDate > dateTo) return false;
      return true;
    });
  }, [ledgerData, dateFrom, dateTo]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo]);

  // Pagination logic
  const totalPages = Math.ceil(filteredLedgerData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredLedgerData.slice(start, start + itemsPerPage);
  }, [filteredLedgerData, currentPage]);

  const currentBusinessSummary = summary.find(s => s.business_id === selectedBusiness);

  // Calculate filtered totals
  const filteredTotals = filteredLedgerData.reduce((acc, entry) => {
    if (entry.amount > 0) acc.inflow += entry.amount;
    else acc.outflow += Math.abs(entry.amount);
    return acc;
  }, { inflow: 0, outflow: 0 });

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page-container animate-slide-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Cash Ledger</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track cash movements for each business
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={!selectedBusiness || ledgerData.length === 0}
            className="text-green-600 border-green-300 hover:bg-green-50"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowRecalcConfirm(true)}
              disabled={recalculating || !selectedBusiness}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid="recalculate-btn"
            >
              <Calculator className="h-4 w-4 mr-2" />
              {recalculating ? 'Recalculating...' : 'Fix Balances'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-[#006241]/10' : ''}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>
      </div>

      {/* Recalculate Confirmation Dialog */}
      <AlertDialog open={showRecalcConfirm} onOpenChange={setShowRecalcConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate Running Balances</AlertDialogTitle>
            <AlertDialogDescription>
              This will recalculate all running balances in chronological order by transaction date.
              Use this to fix incorrect balances caused by out-of-order entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleRecalculateBalances}
              disabled={recalculating}
            >
              <Calculator className="h-4 w-4 mr-2" />
              This Business Only
            </Button>
            <AlertDialogAction
              onClick={handleRecalculateAll}
              disabled={recalculating}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              All Businesses
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Business Selector */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium">Business</Label>
          <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
            <SelectTrigger className="w-[280px]" data-testid="ledger-business-select">
              <SelectValue placeholder="Select business" />
            </SelectTrigger>
            <SelectContent>
              {businesses.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {currentBusinessSummary && (
          <div className="flex items-center gap-2 px-4 py-2 bg-[#006241]/10 rounded-lg">
            <span className="text-sm text-muted-foreground">Balance:</span>
            <span className="font-mono font-bold text-[#006241]">
              {formatCurrency(currentBusinessSummary.current_balance)}
            </span>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="mb-4 border-[#006241]/20" data-testid="filter-panel">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs">From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[150px]"
                  data-testid="filter-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[150px]"
                  data-testid="filter-date-to"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
              <div className="ml-auto flex gap-4 text-sm">
                <div className="px-3 py-1.5 bg-green-50 rounded-lg">
                  <span className="text-muted-foreground">In: </span>
                  <span className="font-mono text-green-700">{formatCurrency(filteredTotals.inflow)}</span>
                </div>
                <div className="px-3 py-1.5 bg-red-50 rounded-lg">
                  <span className="text-muted-foreground">Out: </span>
                  <span className="font-mono text-red-700">{formatCurrency(filteredTotals.outflow)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedBusiness && (
        <Card data-testid="ledger-entries">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">
              Ledger Entries
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {filteredLedgerData.length} entries
            </span>
          </CardHeader>
          <CardContent className="p-0">
            {filteredLedgerData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Wallet className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-[#1A1A18]">No entries found</h3>
                <p className="text-muted-foreground mt-1">
                  {ledgerData.length > 0 ? 'Try adjusting your filters' : 'Ledger entries will appear after cashups are confirmed'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F2F2F0]">
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedData.map((entry) => (
                        <TableRow 
                          key={entry.id}
                          className={isClickable(entry) ? 'cursor-pointer hover:bg-muted/50' : ''}
                          onClick={() => isClickable(entry) && handleEntryClick(entry)}
                        >
                          <TableCell>{getEntryIcon(entry.entry_type)}</TableCell>
                          <TableCell className="font-medium">
                            <span className="inline-flex items-center gap-1">
                              {formatDate(entry.entry_date)}
                              {lockedPeriodFor(entry.business_id, entry.entry_date) && (
                                <Lock className="h-3 w-3 text-muted-foreground" data-testid={`ledger-locked-${entry.id}`} />
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="capitalize text-sm">
                              {entry.entry_type.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                            {entry.description}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${
                            entry.amount >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {entry.amount >= 0 ? '+' : ''}{formatCurrency(entry.amount)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(entry.running_balance)}
                          </TableCell>
                          <TableCell>
                            {isClickable(entry) && (
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-4 border-t">
                    <span className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredLedgerData.length)} of {filteredLedgerData.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          data-testid="first-page-btn"
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          data-testid="prev-page-btn"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          data-testid="next-page-btn"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          data-testid="last-page-btn"
                        >
                          Last
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LedgerPage;
